import { useCallback, FormEvent, ChangeEvent, useState } from 'react';
import locale from '@glu/locale';
import { MagnifyAshIcon, XCircleAshIcon } from '@glu/icons-react';
import { Input } from '@glu/form-components';
import { Button } from '@glu/buttons-react';
import { Locale } from '@glu/locale-react';
import { Typography } from '@glu/typography-react';
import useStyles from './Search.styles';

/**
 * Props for the search component.
 */
export interface ISearchProps {
  /**
   * Submit handler for the form that wraps the search input.
   */
  onSearchSubmit(filterString: string): void;
}

/**
 * Passed as the name and data-qa attribute for the search input
 */
const SEARCH_INPUT_NAME = 'grid-search-input';

const DEFAULT_SEARCH_TEXT = '';

/**
 * Search icon displayed in the controlbar for the ComposedViewAgGrid. When closed will show a
 * magnifying glass that when clicked on will open a search input. When a value is entered in the
 * input the onSearchSubmit function will be executed. Next to the input is an X icon that is only
 * shown if the search input is displayed. When clicked on the X icon will close the search input.
 */
const Search = ({ onSearchSubmit }: ISearchProps) => {
  const [searchText, setSearchText] = useState(DEFAULT_SEARCH_TEXT);
  const [currentSearch, setCurrentSearch] = useState('');
  const classes = useStyles();

  const onSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      onSearchSubmit(searchText);
      setCurrentSearch(searchText);
    },
    [onSearchSubmit, searchText]
  );

  const onChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  }, []);

  const onClear = useCallback(() => {
    onSearchSubmit('');
    setSearchText('');
    setCurrentSearch('');
  }, [onSearchSubmit]);

  return (
    <div className={classes.wrapper}>
      <form className={classes.form} onSubmit={onSubmit}>
        <Input
          data-qa={SEARCH_INPUT_NAME}
          disableInputAPI
          labelText={locale.get('dataComponents.search')}
          name={SEARCH_INPUT_NAME}
          onChange={onChange}
          placeholder={locale.get('dataComponents.searchInputPlaceholder')}
          screenReaderLabel
          type="text"
          value={searchText}
        />
        {searchText && (
          <XCircleAshIcon
            actionable={{
              'aria-label': locale.get('dataComponents.clearSearchInput'),
              title: locale.get('dataComponents.clearSearchInput'),
              onClick: () => setSearchText(''),
            }}
            className={classes.clearSearchInputIcon}
            data-qa="clearSearchInputButton"
            title={locale.get('dataComponents.clearSearchInput')}
          />
        )}
        <MagnifyAshIcon
          actionable={{
            'aria-label': locale.get('dataComponents.search'),
            title: locale.get('dataComponents.search'),
            type: 'submit',
          }}
          className={classes.searchIcon}
          data-qa="searchButton"
          title={locale.get('dataComponents.search')}
        />
        {currentSearch && (
          <div className={classes.resultsForWrapper}>
            <Typography className={classes.resultsForText} variant="small-text">
              <Locale
                localeKey="dataComponents.resultsFor"
                params={[<strong className={classes.currentSearch}>{currentSearch}</strong>]}
              />
            </Typography>
            <Button
              className={classes.clearSearch}
              onClick={onClear}
              size="extraSmall"
              variant="tertiary"
            >
              {locale.get('dataComponents.clearSearch')}
            </Button>
          </div>
        )}
      </form>
    </div>
  );
};

export default Search;
